// Operation contract for Tweedia edits (#400) — client half.
//
// `operations.json` (this module's sibling) is a byte-identical mirror of the
// canonical registry at packages/functions/shared/operations.json. Each
// package's deploy bundle is isolated (CRA only bundles files under src/, the
// container only sees its own Docker build context, firebase deploy only
// uploads packages/functions), so a single physical file is impossible. Run
// `node scripts/sync-operations-registry.js` after editing the canonical copy;
// operations.test.js fails if the copies ever drift.
//
// This module deliberately mirrors the Python evaluator in
// packages/functions/shared/operations.py function-for-function. The client is
// NEVER authoritative — an operation with an `entitlement` is re-evaluated in
// the render worker against the account's real entitlements — but the client
// needs the same answers to render honest controls and to send a coherent
// `settings.operations` block.
//
// Being a re-implementation, no hash can police it: the registry mirrors are
// byte-identical, this file is not. `operations.cases.json` (mirrored the same
// way) is the shared conformance fixture — every case in it is replayed here
// and against the Python evaluator, so a rule that drifts between the two
// languages fails a test instead of shipping.
//
// Display names come from operationCopy.js (#379), which stays the single
// naming source; the registry carries a copy of the string only so the Python
// side (which cannot import JS) has one, and the parity test below asserts
// they match.

import registry from './operations.json';
import { OPERATIONS as OPERATION_COPY } from './operationCopy';

export const SCHEMA_VERSION = registry.schemaVersion;

// Ordered by pipeline position, so any UI that lists operations renders the
// real ffmpeg chain order rather than inventing its own.
export const OPERATIONS = [...registry.operations].sort(
  (a, b) => a.pipelinePosition - b.pipelinePosition
);

export const OPERATION_IDS = OPERATIONS.map((op) => op.id);

// The operations the Edits cards render, in pipeline order (#401).
//
// `copyKey` is the discriminator on purpose: it is non-null exactly for the
// operations that have a user-facing name in operationCopy.js, and null for the
// two the user cannot yet address (output_fps and scale are derived from the
// source and have no control anywhere in the product). So a new operation
// appears on the Edits tab, at its real position in the ffmpeg chain, the
// moment it has copy — no list to hand-sort and no ordering to keep in step.
export const OPERATIONS_BY_UI_ORDER = OPERATIONS.filter((op) => !!op.copyKey);

const BY_ID = OPERATIONS.reduce((acc, op) => {
  acc[op.id] = op;
  return acc;
}, {});

export const TRIM_STILLNESS = 'trim_stillness';
export const VARIABLE_SPEED = 'variable_speed';
export const OUTPUT_FPS = 'output_fps';
export const SCALE = 'scale';
export const DEFLICKER = 'deflicker';
export const TRANSITIONS = 'transitions';
export const WATERMARK_REMOVAL = 'watermark_removal';

// The enable layer's own key inside `settings`. Not owned by any operation.
export const OPERATIONS_KEY = 'operations';

export const ALLOWED_SETTINGS_KEYS = new Set([
  OPERATIONS_KEY,
  ...OPERATIONS.flatMap((op) => op.settingsKeys),
]);

export const SETTINGS_KEY_OWNER = OPERATIONS.reduce((acc, op) => {
  op.settingsKeys.forEach((key) => {
    acc[key] = op.id;
  });
  return acc;
}, {});

export const getOperation = (id) => BY_ID[id];

// The `config/featureFlags` field an operation is gated by, and what it means
// when that field is missing. Read from the registry rather than typed out at
// the call site: the flag NAME lives in the same entry as the gate that
// consumes it, so a UI that hides a dark operation and the resolver that would
// have refused it cannot end up reading different flags.
export const featureFlagOf = (id) => (BY_ID[id] ? BY_ID[id].featureFlag : null) || null;
export const featureFlagDefaultOf = (id) =>
  Boolean(BY_ID[id] && BY_ID[id].featureFlagDefault);

export const ownerOf = (settingsKey) => SETTINGS_KEY_OWNER[settingsKey];

// User-facing name, taken from the naming module when the operation has a copy
// entry (all the ones a user can actually see do).
export const displayNameOf = (id) => {
  const op = BY_ID[id];
  if (!op) return undefined;
  const copy = op.copyKey ? OPERATION_COPY[op.copyKey] : undefined;
  return copy ? copy.displayName : op.displayName;
};

// A STRICT decimal literal, deliberately narrower than either language's
// built-in parser. Number() and Python's int()/float() disagree on almost every
// interesting string ('1920.0' is 1920 here and raises there; '0x10' is 16 here
// and raises there; 'Infinity' is rejected here and accepted there), and this
// module is a second implementation of one contract — so neither language's
// parser can BE the contract. These are. Mirrors _POSITIVE_*_STRING in
// packages/functions/shared/operations.py; operations.cases.json pins both.
const POSITIVE_INT_STRING = /^[0-9]+$/;
const POSITIVE_NUMBER_STRING = /^[0-9]+(\.[0-9]+)?$/;

// Coerce to a positive number, or null if it isn't one. Booleans are rejected
// up front (both languages would otherwise read true as 1); real numbers must
// be finite and are truncated toward zero in integral mode (Math.trunc and
// Python's int() agree on every finite float); strings are trimmed and must
// then be a plain decimal literal; everything else (null, objects, ...) is
// invalid. See _coerce_positive() in shared/operations.py.
const coercePositive = (value, integral) => {
  let number;
  if (typeof value === 'boolean') return null;
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null;
    number = value;
  } else if (typeof value === 'string') {
    const text = value.trim();
    const pattern = integral ? POSITIVE_INT_STRING : POSITIVE_NUMBER_STRING;
    if (!pattern.test(text)) return null;
    number = Number(text);
  } else {
    return null;
  }
  const coerced = integral ? Math.trunc(number) : number;
  return coerced > 0 ? coerced : null;
};

// Derive the enable state from the pre-#400 settings keys alone. Must
// reproduce exactly what the worker did before `settings.operations` existed.
const legacyEnabled = (op, settings) => {
  const descriptor = op.enable || { mode: 'always' };
  switch (descriptor.mode) {
    case 'always':
      return true;
    case 'bool':
      return Boolean(settings[descriptor.key]);
    case 'boolDefaultTrue':
      return settings[descriptor.key] === undefined
        ? true
        : Boolean(settings[descriptor.key]);
    case 'positiveNumber': {
      const raw = settings[descriptor.key] === undefined
        ? (descriptor.absentDefault ?? 0)
        : settings[descriptor.key];
      return coercePositive(raw, false) !== null;
    }
    case 'allPositiveInts':
      return descriptor.keys.every((key) => coercePositive(settings[key], true) !== null);
    default:
      throw new Error(`Unknown enable mode ${descriptor.mode} for operation ${op.id}`);
  }
};

// What the CLIENT is asking for — before entitlements and flags. An explicit
// boolean in settings.operations.<id>.enabled wins.
export const requestedEnabled = (op, settings) => {
  const s = settings || {};
  const block = s[OPERATIONS_KEY];
  if (block && typeof block === 'object') {
    const entry = block[op.id];
    if (entry && typeof entry === 'object' && typeof entry.enabled === 'boolean') {
      return entry.enabled;
    }
  }
  return legacyEnabled(op, s);
};

// Operations that cannot both run on one edit. `members` is a precedence
// order: the earliest enabled member wins and the rest are forced off. See the
// registry's $doc for why MotionPace outranks MotionTrim. Mirrors
// EXCLUSIVE_GROUPS in shared/operations.py.
export const EXCLUSIVE_GROUPS = registry.exclusiveGroups || [];

// Whether either gate would turn `op` off. Mirrors _gates_refuse in
// shared/operations.py.
const gatesRefuse = (op, ent, flg) => {
  if (op.featureFlag) {
    const fallback = Boolean(op.featureFlagDefault);
    if (!(flg[op.id] === undefined ? fallback : flg[op.id])) return true;
  }
  return Boolean(op.entitlement) && !ent[op.entitlement];
};

// Hand an exclusive group back to its runner-up when a GATE kills the leader
// (#585).
//
// THE BUG THIS EXISTS FOR. The editor's Motion selector is exclusive, so
// rendering persists BOTH booleans: `variable_speed.enabled: true` next to
// `trim_stillness.enabled: false`. MotionPace is the default mode for new
// projects, so that is now the shape every rendered project carries. Turn the
// `motionPace` flag off and the blob resolved to NEITHER mode — the flag gate
// killed MotionPace, the persisted `false` killed MotionTrim — and both the
// editor and the next render came out with no stillness handling at all.
//
// THE RULE, and why it needs no provenance tracking. A member that was
// REQUESTED but lost to EITHER of its gates voids the enable-layer entries of
// every member ranked below it, which then resolve from `legacyEnabled` as if
// `settings.operations` had never named them. An enable-layer `false` sitting
// beside a `variable_speed: true` is DEFINITIONALLY the exclusive-group loser,
// because writing the winner is the only thing that writes it. A genuine
// "Motion off" choice has `variable_speed` false or absent, so this never fires
// for one.
//
// BOTH GATES, not just the flag. The reasoning above is about WHO WROTE the
// loser entry, and the selector wrote it whichever gate later killed the
// leader — so an operation refused by `entitlements` strands the identical
// orphaned `false` and would render full length and untrimmed, bug #585
// verbatim through the other door. Nothing in the registry can reach that today
// (no member of the `stillness` group declares an `entitlement`), but
// `flagFallback` is declared on the GROUP, so the next gated member would
// otherwise inherit a silent version of the bug.
//
// Voiding is not forcing on: the runner-up re-derives from the legacy keys and
// then faces its own gates, so this cannot enable something a gate would
// refuse. `trim_stillness` is enable.mode `always`, which is why it comes back.
//
// NOTHING IS WRITTEN — resolution-time only, so re-lighting the flag restores
// MotionPace exactly. Mirrors _apply_gate_fallback in shared/operations.py.
const applyGateFallback = (resolved, gateShadowed, settings, ent, flg) => {
  EXCLUSIVE_GROUPS.forEach((group) => {
    if (!group.flagFallback) return;
    const members = (group.members || []).filter((m) => m in resolved);
    members.forEach((member, rank) => {
      if (!gateShadowed[member]) return;
      members.slice(rank + 1).forEach((loser) => {
        if (resolved[loser]) return;
        const op = BY_ID[loser];
        // Off by its OWN tuning key is a real choice, not the loser entry.
        if (!legacyEnabled(op, settings)) return;
        if (gatesRefuse(op, ent, flg)) return;
        resolved[loser] = true;
      });
    });
  });
  return resolved;
};

// Force off every member of a group but its highest-precedence survivor. Runs
// LAST, on the post-gate map, so it only ever arbitrates between operations
// that were already going to run.
const applyExclusiveGroups = (resolved) => {
  EXCLUSIVE_GROUPS.forEach((group) => {
    const survivors = (group.members || []).filter(
      (member) => resolved[member] === true
    );
    survivors.slice(1).forEach((loser) => {
      resolved[loser] = false;
    });
  });
  return resolved;
};

// The enable state of every operation, id -> bool. The two gates below and the
// exclusive groups after them can only ever turn an operation OFF, so a
// plumbing bug fails closed — same posture as should_watermark() on the worker.
//
// THE ONE EXCEPTION is `applyGateFallback` (#585), which can re-enable the
// runner-up of an exclusive group whose leader was requested and then killed by
// EITHER of its gates — the loser entry beside that request was written by the
// selector, not chosen by a user. The promoted operation still faces both of
// its own gates. See that function.
export const resolveOperations = (settings, entitlements, flags) => {
  const s = settings || {};
  const ent = entitlements || {};
  const flg = flags || {};

  // Which operations were asked for and lost to a GATE — feature flag or
  // entitlement. `resolved` collapses that and "never asked for" into one
  // false, and the fallback has to tell them apart. Both gates count: the
  // exclusive-group loser entry beside the request was written by the selector
  // whichever one did the killing.
  const gateShadowed = {};

  const resolved = OPERATIONS.reduce((acc, op) => {
    let enabled = requestedEnabled(op, s);
    if (enabled && op.featureFlag) {
      const fallback = Boolean(op.featureFlagDefault);
      const flagValue = flg[op.id] === undefined ? fallback : flg[op.id];
      if (!flagValue) {
        enabled = false;
        gateShadowed[op.id] = true;
      }
    }
    if (enabled && op.entitlement && !ent[op.entitlement]) {
      enabled = false;
      gateShadowed[op.id] = true;
    }
    acc[op.id] = enabled;
    return acc;
  }, {});

  // Before the exclusive groups, not after: the fallback can turn the runner-up
  // ON, and the groups must then arbitrate the result exactly as they would any
  // other. Running it afterwards could leave two members of one group enabled.
  return applyExclusiveGroups(
    applyGateFallback(resolved, gateShadowed, s, ent, flg)
  );
};

export const isOperationEnabled = (id, settings, entitlements, flags) =>
  resolveOperations(settings, entitlements, flags)[id];

// Whether this edit needs stored motion analysis to render at all. False means
// the worker synthesizes one full-length segment instead of failing on a
// missing video.motionSegments (#383).
export const requiresMotionAnalysis = (settings, entitlements, flags) => {
  const resolved = resolveOperations(settings, entitlements, flags);
  return OPERATIONS.some((op) => op.requiresMotionAnalysis && resolved[op.id]);
};

// Whether this edit COULD need motion analysis — asked before the gates.
// requiresMotionAnalysis answers "does the render, as resolved, consume motion
// data"; this answers "does the request contain an operation that consumes
// it", ignoring entitlements and flags. The gates only ever turn operations
// OFF, so anything that decides what to KEEP (rather than what to run) has to
// ask pre-gate or it discards the input an entitled render will need. Mirrors
// requests_motion_analysis() in shared/operations.py.
export const requestsMotionAnalysis = (settings) => {
  const s = settings || {};
  return OPERATIONS.some((op) => op.requiresMotionAnalysis && requestedEnabled(op, s));
};

// Build the `settings.operations` enable layer for a settings blob.
//
// Projected from the legacy tuning keys ALONE, so the block it writes can never
// contradict the keys sitting next to it — the point is to make the enable state
// explicit and addressable (#401 flips these), not to change any behavior.
// Entitlement-gated operations are deliberately resolved WITHOUT entitlements
// here: the client persists the user's request, and the worker applies the
// account's authority at render time.
//
// Deliberately legacyEnabled, NOT requestedEnabled: callers re-derive from an
// already-persisted settings blob, which carries this function's own previous
// output. Consulting `settings.operations` would let that stale block outrank
// the fresh tuning keys and silently re-persist the user's OLD choice, which
// the worker then honours (its resolve() puts the block first).
//
// `overrides` ({ [operationId]: boolean }) is the ONE exception, and #401 is why
// it exists: an operation whose `enable` descriptor is `always` — trim_stillness
// — has no tuning key capable of expressing "off", so the enable layer is the
// only place that choice can live. The Edits cards pass the user's explicit
// switch state here; every operation not named in `overrides` stays projected
// from its tuning keys exactly as before, so omitting the argument is a no-op.
export const buildOperationsBlock = (settings, overrides) => {
  const s = settings || {};
  const explicit = overrides || {};
  return OPERATIONS.reduce((acc, op) => {
    const override = explicit[op.id];
    acc[op.id] = {
      enabled: typeof override === 'boolean' ? override : legacyEnabled(op, s),
    };
    return acc;
  }, {});
};

export default OPERATIONS;
